import { useState, useEffect, useCallback } from 'react';
import { getAPIUrl } from '../../config/api.config';
import { logger } from '../../utils/logger';
import { useAuth } from '../firebase/useAuth';
import { getAuthTokenAsync } from '../../services/api/authTokenManager';

export interface PaymentHistoryItem {
  orderId: string;
  planId: string;
  amount: number;
  currency: string;
  status: string;
  customerDetails: {
    name: string;
    email: string;
    phone: string;
  };
  createdAt: string;
  updatedAt?: string;
  paymentMethod?: string;
  description?: string;
}

export interface PaymentHistoryData {
  payments: PaymentHistoryItem[];
  totalPayments: number;
  pendingPayments: number;
  completedPayments: number;
}

interface UsePaymentHistoryResult {
  paymentHistory: PaymentHistoryData | null;
  loading: boolean;
  error: string | null;
  refreshPaymentHistory: () => Promise<void>;
  resumePayment: (orderId: string) => Promise<string>; // Returns redirect URL or success message
}

export function usePaymentHistory(): UsePaymentHistoryResult {
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const API_BASE_URL = getAPIUrl();

  const fetchPaymentHistory = useCallback(async () => {
    if (!user?.uid) {
      setPaymentHistory(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const authToken = await getAuthTokenAsync();
      const response = await fetch(`${API_BASE_URL}/api/payments/user/${user.uid}/payments`, {
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // No payment history found - this is normal for new users
          setPaymentHistory({
            payments: [],
            totalPayments: 0,
            pendingPayments: 0,
            completedPayments: 0
          });
          return;
        }
        throw new Error(`Failed to fetch payment history: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        setPaymentHistory(result.data);
      } else {
        throw new Error(result.error || 'Failed to fetch payment history');
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch payment history';
      setError(errorMessage);
      logger.error('Payment history fetch error', { error: err, userId: user.uid });
    } finally {
      setLoading(false);
    }
  }, [user?.uid, API_BASE_URL]);

  const resumePayment = useCallback(async (orderId: string): Promise<string> => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      logger.info('Attempting to resume payment', { orderId, userId: user.uid });
      
      const resumeToken = await getAuthTokenAsync();
      const response = await fetch(`${API_BASE_URL}/api/payments/resume-payment/${orderId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(resumeToken ? { 'Authorization': `Bearer ${resumeToken}` } : {}),
        },
        body: JSON.stringify({
          userId: user.uid
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to resume payment: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        const redirectUrl = result.data.paymentUrl;
        
        if (redirectUrl) {
          logger.info('Payment resumption successful', { orderId, redirectUrl });
          
          // Refresh payment history after successful resumption
          setTimeout(() => void fetchPaymentHistory(), 1000);
          
          return redirectUrl;
        } else {
          throw new Error('No payment URL received from server');
        }
      } else {
        throw new Error(result.error || 'Failed to resume payment');
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resume payment';
      logger.error('Payment resumption error', { error: err, orderId, userId: user.uid });
      throw new Error(errorMessage);
    }
  }, [user?.uid, API_BASE_URL, fetchPaymentHistory]);

  const refreshPaymentHistory = useCallback(async () => {
    await fetchPaymentHistory();
  }, [fetchPaymentHistory]);

  useEffect(() => {
    if (user?.uid) {
      void fetchPaymentHistory();
    }
  }, [user?.uid, fetchPaymentHistory]);

  return {
    paymentHistory,
    loading,
    error,
    refreshPaymentHistory,
    resumePayment
  };
}

// Utility function to get readable payment status
export function getPaymentStatusText(status: string): string {
  const statusMap: Record<string, string> = {
    'pending': 'Menunggu Pembayaran',
    'settlement': 'Berhasil',
    'capture': 'Berhasil',
    'deny': 'Ditolak',
    'cancel': 'Dibatalkan',
    'expire': 'Kedaluwarsa',
    'failure': 'Gagal'
  };
  
  return statusMap[status] || status;
}

// Utility function to get status color for UI
export function getPaymentStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    'pending': 'bg-amber-100/60 text-amber-800 border-amber-200/50',
    'settlement': 'bg-green-100/60 text-green-800 border-green-200/50',
    'capture': 'bg-green-100/60 text-green-800 border-green-200/50',
    'deny': 'bg-red-100/60 text-red-800 border-red-200/50',
    'cancel': 'bg-slate-100/60 text-slate-800 border-slate-200/50',
    'expire': 'bg-slate-100/60 text-slate-800 border-slate-200/50',
    'failure': 'bg-red-100/60 text-red-800 border-red-200/50'
  };
  
  return colorMap[status] || 'bg-slate-100/60 text-slate-800 border-slate-200/50';
}

// Utility function to check if payment can be resumed
export function canResumePayment(status: string): boolean {
  return status === 'pending';
}